<template>
  <StepCard title="Dados Bancários">
    <v-row>
      <v-col cols="12">
        <v-checkbox
          v-model="eContaDeTerceiro"
          label="A conta bancária pertence a um terceiro?"
          density="compact"
          hide-details
        ></v-checkbox>
        <v-alert
          v-if="eContaDeTerceiro"
          type="info"
          variant="tonal"
          density="compact"
          class="mt-3"
          rounded="lg"
        >
          <strong>Importante:</strong> É necessário anexar um documento (Imagem ou PDF) contendo a autorização do titular da conta bancária.
        </v-alert>
      </v-col>
    </v-row>

    <v-row v-if="!showManual">
      <v-col cols="12" sm="4">
        <v-autocomplete label="Banco" v-model="formData.bankId" :items="banks" item-title="nome"
          item-value="id" :loading="banksLoading" variant="outlined" density="compact" hide-no-data
          :error-messages="formErrors.bankId">
          <template v-slot:item="{ props, item }">
            <v-list-item v-bind="props" :title="`${item.raw.codigo} - ${item.raw.nome}`"></v-list-item>
          </template>
        </v-autocomplete>
      </v-col>
      <v-col cols="12" sm="4">
        <v-autocomplete label="Agência" v-model="formData.agencyId" :items="agencies" item-title="nome"
          item-value="id" :loading="agenciesLoading" :disabled="!formData.bankId" variant="outlined" hide-no-data
          density="compact" :error-messages="formErrors.agencyId">
          <template v-slot:item="{ props, item }">
            <v-list-item v-bind="props" :title="`${item.raw.codigo} - ${item.raw.nome}`"></v-list-item>
          </template>
        </v-autocomplete>
      </v-col>
      <v-col cols="12" sm="4">
        <v-text-field 
          label="Conta" 
          v-model="formattedBankAccount"
          variant="outlined" 
          density="compact"
          :error-messages="formErrors.bankAccount" 
          :disabled="!formData.agencyId"
        ></v-text-field>
      </v-col>
    </v-row>
    <v-row v-if="showManual">
     <v-col cols="12" sm="4">
    <v-text-field 
      label="Cód. Banco (Ex: 260)" 
      v-model="formData.manualBankName" 
      variant="outlined" 
      density="compact"
      inputmode="numeric" 
      placeholder="Digite apenas números"
      :error-messages="formErrors.manualBankName"
      @input="(e) => formData.manualBankName = e.target.value.replace(/\D/g, '')"
    ></v-text-field>
  </v-col>
  <v-col cols="12" sm="4">
    <v-text-field 
      label="Agência (Sem dígito)" 
      v-model="formData.manualAgencyName"
      variant="outlined" 
      density="compact"
      inputmode="numeric"
      placeholder="Digite apenas números"
      :error-messages="formErrors.manualAgencyName"
      @input="(e) => formData.manualAgencyName = e.target.value.replace(/\D/g, '')"
    ></v-text-field>
  </v-col>
  <v-col cols="12" sm="4">
    <v-text-field 
      label="Conta" 
      v-model="formData.manualBankAccount"
      variant="outlined" 
      density="compact"
      :error-messages="formErrors.manualBankAccount"
    ></v-text-field>
  </v-col>
    </v-row>

    <v-row v-if="formData.titularidadeConta === 'terceiro'">
      <v-col cols="12" sm="6">
        <v-text-field
          label="Nome completo do favorecido"
          v-model="formData.nomeFavorecido"
          variant="outlined"
          density="compact"
          :error-messages="formErrors.nomeFavorecido"
        ></v-text-field>
      </v-col>
      <v-col cols="12" sm="6">
        <v-text-field
          label="CPF do favorecido"
          v-model="formData.cpfFavorecido"
          variant="outlined"
          density="compact"
          inputmode="numeric"
          placeholder="Somente números"
          :error-messages="formErrors.cpfFavorecido"
          @input="(e) => formData.cpfFavorecido = e.target.value.replace(/\D/g, '')"
        ></v-text-field>
      </v-col>
    </v-row>

    <v-row v-if="formData.titularidadeConta === 'terceiro'">
      <v-col cols="12">
        <v-file-input
          v-model="formData.arquivoAutorizacao"
          label="Anexar autorização para depósito em conta de terceiro"
          variant="outlined"
          density="compact"
          :error-messages="formErrors.arquivoAutorizacao"
          accept="image/*, application/pdf"
        >
          <template v-slot:selection="{ fileNames }">
            <template v-for="fileName in fileNames" :key="fileName">
              <v-chip size="small" label color="primary" class="me-2">
                {{ fileName }}
              </v-chip>
            </template>
          </template>
        </v-file-input>
      </v-col>
    </v-row>

    <v-row>
      <v-col cols="12" class="d-flex flex-wrap ga-2">
        <v-btn v-if="!showManual" color="primary" @click="toggleManual">
          Dados Bancários Não Encontrados
        </v-btn>
        <v-btn v-else color="secondary" @click="toggleManual">
          Cancelar inserção manual
        </v-btn>
        <v-btn color="primary" @click="showAuthorizationModel = true">
          Modelo de Autorização
        </v-btn>
      </v-col>
    </v-row>

    <v-alert v-if="submissionError" type="error" density="compact" class="mt-4" :text="submissionError">
    </v-alert>
  </StepCard>

  <v-dialog v-model="showAuthorizationModel" max-width="600">
    <v-card>
      <v-card-title class="text-h6 bg-primary text-white">
        Modelo de Autorização
      </v-card-title>
      <v-card-text class="pa-4">
        <div class="text-body-1 font-weight-bold mb-4 text-center">
          AUTORIZAÇÃO PARA CRÉDITO EM CONTA BANCÁRIA DE TERCEIROS
        </div>
        <div class="text-body-2 mb-4 text-justify">
          Eu, [NOME DO TITULAR DO PLANO], inscrito no CPF sob o nº [CPF], titular do plano de saúde, autorizo expressamente a CASSEMS a realizar o crédito referente a este reembolso de despesas médicas na conta bancária de terceiros abaixo identificada:
        </div>
        
        <div class="font-weight-bold mt-4 mb-2">DADOS DO FAVORECIDO (QUEM RECEBE):</div>
        <div class="mb-1">Nome Completo: _________________________________________________</div>
        <div class="mb-4">CPF: __________________________</div>

        <div class="font-weight-bold mt-4 mb-2">DADOS BANCÁRIOS:</div>
        <div class="mb-4">Banco: _________________ Agência: _________ Conta: ____________</div>

        <div class="mt-6 mb-8">
          [Cidade - UF], ____ de ____________ de 2025.
        </div>

        <div class="text-center mt-8">
          <div style="border-top: 1px solid black; width: 80%; margin: 0 auto;"></div>
          <div class="mt-2">Assinatura</div>
          <div>Nome Completo do Titular do Plano de Saúde</div>
        </div>
      </v-card-text>
      <v-card-actions>
        <v-btn color="primary" variant="elevated" @click="copiarModelo">
          <v-icon start>{{ copiado ? 'mdi-check' : 'mdi-content-copy' }}</v-icon>
          {{ copiado ? 'Copiado!' : 'Copiar Modelo' }}
        </v-btn>
        <v-spacer></v-spacer>
        <v-btn color="primary" variant="elevated" @click="showAuthorizationModel = false">Fechar</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script setup>
import { computed } from 'vue';
import StepCard from './StepCard.vue';
import { ref } from 'vue';

// Computed property para fazer a ponte entre o checkbox booleano e a string 'titular'/'terceiro'
const eContaDeTerceiro = computed({
  get: () => formData.value.titularidadeConta === 'terceiro',
  set: (newValue) => {
    formData.value.titularidadeConta = newValue ? 'terceiro' : 'titular';
  }
});

const formData = defineModel({ type: Object, required: true });
const formattedBankAccount = defineModel('formattedBankAccount', { type: String, required: true });
const showManual = defineModel('showManual', { type: Boolean, default: false });

const showAuthorizationModel = ref(false);
const copiado = ref(false);

const copiarModelo = () => {
  const texto = `AUTORIZAÇÃO PARA CRÉDITO EM CONTA BANCÁRIA DE TERCEIROS

Eu, [NOME DO TITULAR DO PLANO], inscrito no CPF sob o nº [CPF], titular do plano de saúde, autorizo expressamente a CASSEMS a realizar o crédito referente a este reembolso de despesas médicas na conta bancária de terceiros abaixo identificada:

DADOS DO FAVORECIDO (QUEM RECEBE):
Nome Completo: _________________________________________________
CPF: __________________________

DADOS BANCÁRIOS:
Banco: _________________ Agência: _________ Conta: ____________

[Cidade - UF], ____ de ____________ de 2025.

_________________________________________________
Assinatura
Nome Completo do Titular do Plano de Saúde`;

  navigator.clipboard.writeText(texto).then(() => {
    copiado.value = true;
    setTimeout(() => {
      copiado.value = false;
    }, 2000);
  });
};

const toggleManual = () => {
  showManual.value = !showManual.value;
};

defineProps({
  formErrors: { type: Object, required: true },
  banks: { type: Array, required: true },
  agencies: { type: Array, required: true },
  banksLoading: { type: Boolean, default: false },
  agenciesLoading: { type: Boolean, default: false },
  submissionError: { type: String, default: '' },
});
</script>